import React, { FC, ReactNode, useMemo } from 'react'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { createAppTheme } from '@/apps/latest/shared/styles/theme'
import { LoginPage } from './pages/login'
import { AppProvider } from './stores/store'
import KnowledgePage from '@/pages/knowledge'
import MessagesPage from '@/pages/messages'
import {
  HomecareHome,
  HomecarePatient,
  HomecarePlan,
  HomecareVisit,
} from '@/pages/homecare'
import { useCheckAuthQuery } from '@/shared/hooks/queries/useAuthMutations'
import { useLocalhostRedirect } from '@/shared/hooks/useLocalhostRedirect'
import { AdaptationPage } from './pages/adaptation'
import { MicPermissionHelpPage } from './pages/micPermissionHelp'
import { ErrorBoundary } from './pages/error'
import { LogoutPage } from './pages/logout'
import { AppTypeProvider, useAppType } from '@/shared/contexts/AppTypeContext'
import {
  AnyCompassEmbedProvider,
  EmbedSessionGate,
  embedSessionRecovery,
} from '@/features/anycompass'
import { AccessDeniedGate } from '@/features/auth'
import {
  FontScaleProvider,
  useFontScale,
} from '@/shared/contexts/FontScaleContext'
import { APP_TYPES } from '@/types/appType'
import { LatestToastProvider } from '@/apps/latest/shared/contexts/ToastProvider'
import { LegacyToastProvider } from '@/apps/legacy/shared/contexts/ToastProvider'

// 管理ポータル dispatcher (= useAppType() で legacy/latest 振り分け). design/14 参照.
import {
  PortalHomeDispatcher,
  PortalPatientsDispatcher,
  PortalPharmacistsDispatcher,
  PortalSoapSettingsDispatcher,
  PortalSoapMastersDispatcher,
  PortalSoapRulesDispatcher,
  PortalTracingReportsDispatcher,
  PortalDisplaySettingsDispatcher,
  PortalTutorialDispatcher,
  PortalTextReplacementsDispatcher,
  PortalLineSettingsDispatcher,
  PortalFormatInsertSettingsDispatcher,
  PortalNSIPSExportDispatcher,
  PortalSoapTemplateListDispatcher,
  PortalDrugDetailDispatcher,
  PortalDrugGroupDetailDispatcher,
  PortalDrugGroupCreateDispatcher,
  PortalDrugGroupDrugsDispatcher,
  PortalMetricsDispatcher,
  PortalKasanAnalysisDispatcher,
  PortalMetricsUsageDispatcher,
  PortalGuidanceAnalysisDispatcher,
  PortalMetricsRecordsDispatcher,
  PortalMetricsDetailDispatcher,
} from './pages/portal-dispatchers'

// 系統 dispatcher (= useIsDietitian() で薬剤師 / 管理栄養士 振り分け). yakureki-back log/224 参照.
import {
  GroupRecordingDispatcher,
  HomeDispatcher,
  NewRecordingDispatcher,
  NutritionKarteDispatcher,
  NutritionHomeDispatcher,
  NutritionNewDispatcher,
  SoapDispatcher,
} from './pages/profession-dispatchers'

// Kakeru君 困ったときガイド (静的ページ。法人別の連絡先は variant が持つ)
import { KakeruGuidePage } from './pages/guide'
import { GuideVariant } from './pages/guide/core'
import {
  KakeruGuideVariant,
  kakeruGuidePath,
} from './pages/guide/companySupport'
import { defaultVariant as kakeruGuideDefault } from './pages/guide/variants/default'
import { aiseiVariant as kakeruGuideAisei } from './pages/guide/variants/aisei'

// 遠隔服薬指導のブース (据え置き端末。頼む店のアカウントでログインしたまま。back log/250)
import BoothPage from '@/apps/latest/pages/booth'
import CounselPage from '@/apps/latest/pages/counsel'

// 外部アプリ (= NSIPS) からの deep-link entry. queue_number で yakureki を
// 引いて /soap/:id に history state 経由で autoStart=true を渡す.
import QuickRecordPage from './pages/yakureki/quick-record'

// AppType 駆動 Toast dispatcher. AppTypeProvider 内側に配置し、 user.use_legacy_ui flag
// に応じて Notion 黒トースト (latest) ↔ filled MuiAlert (legacy) を切替える.
//   - LATEST → LatestToastProvider (= apps/latest/shared/contexts/ToastProvider)
//   - LEGACY (default + fallback) → LegacyToastProvider (= apps/legacy/shared/contexts/ToastProvider)
// shared/contexts/ToastContext は API contract only. UI 実装は各 app 階層内で完結.
// 困ったときガイドの版名 → ページ。版名の正典は companySupport.ts で、Record なので
// 版を足してページを足し忘れると型検査で落ちる。
const KAKERU_GUIDE_PAGES: Record<KakeruGuideVariant, GuideVariant> = {
  default: kakeruGuideDefault,
  aisei: kakeruGuideAisei,
}

const ToastByAppType: FC<{ children: ReactNode }> = ({ children }) => {
  const { appType } = useAppType()
  if (appType === APP_TYPES.LATEST) {
    return <LatestToastProvider>{children}</LatestToastProvider>
  }
  return <LegacyToastProvider>{children}</LegacyToastProvider>
}

// Router の root element. App 全体の Provider 階層 (= Theme / AppType / Toast / ErrorBoundary)
// を内側に保持し、 Routes を <Outlet> で展開する.
// Data Router (= createBrowserRouter + RouterProvider) では Routes JSX 外で Provider を
// wrap できないため、 root route の element で全 Provider を持つ.
// Toast は AppType 駆動 dispatcher 経由で latest/legacy が切替わる.
// FontScaleProvider (= 文字サイズ拡大モード, D10) 配下で scale を読み、
// theme を再構築する. ThemeProvider の内側は scale 変化で再 mount せず
// theme 値だけ差し替わる.
// gate: Routes の代わりに全面表示するもの (= 埋め込み時のセッション復帰ゲート).
// Provider 階層の内側で差し替えるため、 theme と ErrorBoundary はそのまま効く.
const ThemedShell: FC<{ gate?: ReactNode }> = ({ gate }) => {
  const { scale } = useFontScale()
  const appTheme = useMemo(() => createAppTheme(scale), [scale])
  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <AppTypeProvider>
        <ToastByAppType>
          <ErrorBoundary>{gate ?? <Outlet />}</ErrorBoundary>
        </ToastByAppType>
      </AppTypeProvider>
    </ThemeProvider>
  )
}

const RouterShell: FC = () => {
  useLocalhostRedirect()
  // 埋め込み iframe で cookie が落ちているだけなら、 /login へ送らずゲートを出す.
  // ログインフォームを出すと患者を開くたびにパスワード入力を強いることになる.
  // 復帰手段の実体はここで注入する (認証側は AnyCOMPASS を知らない).
  const { isRecoverableSessionLoss, isAccessDenied, recoverSession, retry } =
    useCheckAuthQuery(embedSessionRecovery)
  return (
    <FontScaleProvider>
      <ThemedShell
        gate={
          isRecoverableSessionLoss ? (
            <EmbedSessionGate onConnect={recoverSession} />
          ) : isAccessDenied ? (
            // 403 は /login へ送らない (ログインし直しても直らない)。log/193 work-log/6 §3-3。
            <AccessDeniedGate onRetry={retry} />
          ) : undefined
        }
      />
    </FontScaleProvider>
  )
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RouterShell />}>
      <Route path="/" element={<HomeDispatcher />} />
      <Route path="/soap" element={<HomeDispatcher />} />
      <Route path="/soap/:requestId" element={<SoapDispatcher />} />
      <Route path="/yakureki/quick-record" element={<QuickRecordPage />} />
      <Route path="/recording/new" element={<NewRecordingDispatcher />} />
      <Route path="/recording/group" element={<GroupRecordingDispatcher />} />
      {/* 管理栄養士 (= is_dietitian): ホーム / 新しいお客様の登録 / お客様のカルテ (今回の有無で URL を替えない) */}
      <Route path="/nutrition" element={<NutritionHomeDispatcher />} />
      <Route
        path="/nutrition/customers/:customerId"
        element={<NutritionKarteDispatcher />}
      />
      <Route path="/nutrition/new" element={<NutritionNewDispatcher />} />
      {/* 在宅 (β)。在宅の患者の登録 / 患者のページ / 計画書 / 訪問のページ (録音・SOAP・訪問報告書)。 */}
      <Route path="/homecare" element={<HomecareHome />} />
      <Route
        path="/homecare/patients/:patientId"
        element={<HomecarePatient />}
      />
      <Route
        path="/homecare/patients/:patientId/plan"
        element={<HomecarePlan />}
      />
      <Route path="/homecare/visits/:requestId" element={<HomecareVisit />} />
      {/* マニュアル照会 (β)。conversationId 無し = 新しい質問。 */}
      <Route path="/knowledge" element={<KnowledgePage />} />
      <Route path="/knowledge/:conversationId" element={<KnowledgePage />} />
      {/* 患者との LINE のやり取り。LINE 連携の設定が済んだ店舗だけ。threadId 無し = 未選択。 */}
      <Route path="/messages" element={<MessagesPage />} />
      <Route path="/messages/:threadId" element={<MessagesPage />} />
      <Route path="/adaptation" element={<AdaptationPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/mic-permission-help" element={<MicPermissionHelpPage />} />

      {/* 管理ポータル (= dispatcher 経由で legacy/latest 振り分け) */}
      <Route path="/portal" element={<PortalHomeDispatcher />} />
      <Route
        path="/portal/display-settings"
        element={<PortalDisplaySettingsDispatcher />}
      />
      <Route
        path="/portal/soap-generation"
        element={<PortalSoapSettingsDispatcher />}
      />
      <Route
        path="/portal/soap-rules"
        element={<PortalSoapRulesDispatcher />}
      />
      <Route
        path="/portal/soap-masters"
        element={<PortalSoapMastersDispatcher />}
      />
      <Route
        path="/portal/tracing-reports"
        element={<PortalTracingReportsDispatcher />}
      />
      <Route
        path="/portal/format-insert-settings"
        element={<PortalFormatInsertSettingsDispatcher />}
      />
      <Route
        path="/portal/text-replacements"
        element={<PortalTextReplacementsDispatcher />}
      />
      <Route path="/portal/patients" element={<PortalPatientsDispatcher />} />
      <Route
        path="/portal/nsips-export"
        element={<PortalNSIPSExportDispatcher />}
      />

      {/* メトリクス関連ルート（ADR-0039準拠）。統計 = 概況/比較/明細の統合ページ、
          旧3ルートは latest 側でリダイレクト */}
      <Route path="/portal/metrics" element={<PortalMetricsDispatcher />} />
      <Route
        path="/portal/metrics/kasan"
        element={<PortalKasanAnalysisDispatcher />}
      />
      <Route
        path="/portal/metrics/usage"
        element={<PortalMetricsUsageDispatcher />}
      />
      <Route
        path="/portal/metrics/guidance"
        element={<PortalGuidanceAnalysisDispatcher />}
      />
      <Route
        path="/portal/metrics/records"
        element={<PortalMetricsRecordsDispatcher />}
      />
      <Route
        path="/portal/metrics/records/:id"
        element={<PortalMetricsDetailDispatcher />}
      />

      {/* 薬剤師管理 */}
      <Route
        path="/portal/pharmacists"
        element={<PortalPharmacistsDispatcher />}
      />

      {/* LINE設定 */}
      <Route
        path="/portal/line-settings"
        element={<PortalLineSettingsDispatcher />}
      />

      {/* チュートリアル（検証患者作成） */}
      <Route path="/portal/tutorial" element={<PortalTutorialDispatcher />} />

      {/* 遠隔服薬指導のブース (要ログイン・全画面) */}
      <Route path="/booth" element={<BoothPage />} />

      {/* がん相談チャットの試作 (認証不要。URL の token を back が照合する) */}
      <Route path="/counsel/:token" element={<CounselPage />} />

      {/* Kakeru君 困ったときガイド (認証不要。「ログインできない」区分を含むため)。
          パスは kakeruGuidePath が組む — リンク帯と同じ関数を通す */}
      {(Object.keys(KAKERU_GUIDE_PAGES) as KakeruGuideVariant[]).map(
        (variant) => (
          <Route
            key={variant}
            path={kakeruGuidePath(variant)}
            element={<KakeruGuidePage {...KAKERU_GUIDE_PAGES[variant]} />}
          />
        )
      )}

      {/* SOAPテンプレート管理機能のルーティング */}
      <Route
        path="/portal/soaps"
        element={<PortalSoapTemplateListDispatcher />}
      />
      <Route
        path="/portal/soaps/groups/create"
        element={<PortalDrugGroupCreateDispatcher />}
      />
      <Route
        path="/portal/soaps/groups/:id"
        element={<PortalDrugGroupDetailDispatcher />}
      />
      <Route
        path="/portal/soaps/groups/:id/drugs"
        element={<PortalDrugGroupDrugsDispatcher />}
      />
      <Route
        path="/portal/soaps/drugs/:id"
        element={<PortalDrugDetailDispatcher />}
      />
    </Route>
  )
)

const App: FC = () => {
  return (
    <AppProvider>
      <AnyCompassEmbedProvider>
        <RouterProvider router={router} />
      </AnyCompassEmbedProvider>
    </AppProvider>
  )
}

export default App
